import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { VERSION_CHECK_EVENT, fetchPublishedBuild, isOutdated } from '@/lib/app-version';
import { hasWorkBeforeUnload } from '@/stores/workDrafts';

const CHECK_INTERVAL_MS = 5 * 60_000;

/**
 * RC-063: confere a build publicada ao voltar para a aba, ao focar a janela e a cada 5 min.
 * Achou versão nova: recarrega na próxima troca de tela (nada digitado se perde) e mostra
 * a faixa para quem fica parada na agenda o dia inteiro.
 * Story 65.45: com trabalho guardado ou não salvo a troca de tela não recarrega; a versão nova
 * fica anotada e a página recarrega na primeira troca de tela depois de salvar ou descartar.
 */
export function AppVersionWatcher({ reload = () => window.location.reload() }: { reload?: () => void }) {
  const location = useLocation();
  const [outdated, setOutdated] = useState(false);
  const firstPath = useRef(location.pathname);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      if (!alive) return;
      if (isOutdated(await fetchPublishedBuild()) && alive) setOutdated(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    window.addEventListener(VERSION_CHECK_EVENT, check);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      window.removeEventListener(VERSION_CHECK_EVENT, check);
    };
  }, []);

  useEffect(() => {
    if (outdated && location.pathname !== firstPath.current && !hasWorkBeforeUnload()) reload();
    firstPath.current = location.pathname;
  }, [location.pathname, outdated, reload]);

  if (!outdated) return null;
  return (
    <div
      role="alert"
      data-testid="app-version-banner"
      className="fixed inset-x-0 top-0 z-[100] flex flex-wrap items-center justify-center gap-3 border-b border-sober-outline bg-sober-surface px-4 py-3 text-so-14 text-sober-text shadow-sm"
    >
      <span>Há uma versão nova do ValiosasCRM. Atualize para continuar com os horários certos.</span>
      <button
        type="button"
        onClick={reload}
        className="inline-flex min-h-11 items-center gap-2 rounded-so-sm bg-sober-primary-fg px-3 py-1.5 text-so-12 font-bold text-white hover:opacity-90 sm:min-h-0"
      >
        <RefreshCw className="size-4" aria-hidden /> Atualizar agora
      </button>
    </div>
  );
}
